import { useRef, useEffect, useState, useMemo } from 'react'
import Stack from 'components/elements/Stack'
import { Body2 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import SectionContainer from 'stories/V1/layouts/SectionContainer'
import styled from 'styled-components'
import { useSpring, animated } from 'react-spring'
import _useTranslation from 'lib/hooks/_useTranslation'

const AnimatedChatBubble = animated(styled.div<{ isUser: boolean }>`
    background: ${({ isUser }) => (isUser ? theme.colors.secondarySolidDark : '#845BC6')};
    border-radius: ${theme.borderRadius.normal};
    padding: 22px 28px;
    display: flex;
    align-items: center;
    gap: 10px;
    max-width: 80%;
    width: fit-content;
    align-self: ${({ isUser }) => (isUser ? 'flex-start' : 'flex-end')};

    p {
        font-family: var(--font-inter);
        font-feature-settings: var(--inter-font-features);
        font-size: 23px;
        font-weight: 550;
        line-height: 28px;
        letter-spacing: -0.2px;
        @media (max-width: 768px) {
            font-size: 14px;
            line-height: 20px;
            letter-spacing: 0.4px;
        }
    }

    @media (max-width: 768px) {
        padding: 10px 12px;
        max-width: 90%;
        border-radius: 12px;
    }
`)

const AnimatedCounterWrapper = animated(styled.div<{ isUser: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    margin: ${({ isUser }) => (isUser ? '5px 0 20px 10px' : '5px 10px 20px auto')};
    width: fit-content;
    height: 24px;
    span,
    p {
        font-size: 17px;
        font-weight: 650;
        line-height: 20px;
        letter-spacing: 0.3px;
        gap: 4px;
        color: ${theme.colors.grayScaleSecondary};
    }

    span {
        color: ${theme.colors.white};
    }
    @media (max-width: 768px) {
        margin: ${({ isUser }) => (isUser ? '5px 0 0 0' : '0px 0px 0px auto')};
        height: 24px;
        p {
            font-size: 14px;
            font-family: var(--font-inter);
            font-weight: 650 !important;
            line-height: 20px;
            letter-spacing: 0.4px;
        }
    }
`)

const ChatContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 4.5% 25px;
    height: 100%;
    @media (max-width: 768px) {
        padding: 0 0px;
        .chat-bubbles {
            gap: 4px !important;
        }
    }
`

const ChatWrapper = styled.div`
    position: relative;
    width: 90%;
    @media (max-width: 1050px) {
        width: 100%;
    }
`

type AnimationKey = 'msg0' | 'msg1' | 'msg2' | 'msg3' | 'msg4' | 'laugh' | 'reply' | 'fire'

const HomepageChat = () => {
    const { t } = _useTranslation('homepage')
    const [scrollProgress, setScrollProgress] = useState(0)
    const [hasAnimated, setHasAnimated] = useState<Record<AnimationKey, boolean>>({
        msg0: false,
        msg1: false,
        msg2: false,
        msg3: false,
        msg4: false,
        laugh: false,
        reply: false,
        fire: false,
    })
    const chatRef = useRef<HTMLDivElement>(null)

    const messages = [
        { text: t('homepage/chatQuestion1'), isUser: true },
        { text: t('homepage/chatQuestion2'), isUser: true },
        { text: t('homepage/chatQuestion3'), isUser: true },
        { text: t('homepage/chatQuestion4'), isUser: true },
        { text: t('homepage/chatQuestion5'), isUser: true },
        { text: t('homepage/chatAnswer'), isUser: false },
    ]

    const thresholds = useMemo(
        () => ({
            msg0: 0.05,
            msg1: 0.1,
            msg2: 0.15,
            msg3: 0.2,
            msg4: 0.25,
            laugh: 0.3,
            reply: 0.35,
            fire: 0.35,
        }),
        []
    )

    const msg0Animation = useSpring({
        opacity: scrollProgress >= thresholds.msg0 || hasAnimated.msg0 ? 1 : 0,
        transform: scrollProgress >= thresholds.msg0 || hasAnimated.msg0 ? 'translateY(0px)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        onRest: () => {
            if (scrollProgress >= thresholds.msg0) {
                setHasAnimated((prev) => ({ ...prev, msg0: true }))
            }
        },
    })

    const msg1Animation = useSpring({
        opacity: scrollProgress >= thresholds.msg1 || hasAnimated.msg1 ? 1 : 0,
        transform: scrollProgress >= thresholds.msg1 || hasAnimated.msg1 ? 'translateY(0px)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        onRest: () => {
            if (scrollProgress >= thresholds.msg1) {
                setHasAnimated((prev) => ({ ...prev, msg1: true }))
            }
        },
    })

    const msg2Animation = useSpring({
        opacity: scrollProgress >= thresholds.msg2 || hasAnimated.msg2 ? 1 : 0,
        transform: scrollProgress >= thresholds.msg2 || hasAnimated.msg2 ? 'translateY(0px)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        onRest: () => {
            if (scrollProgress >= thresholds.msg2) {
                setHasAnimated((prev) => ({ ...prev, msg2: true }))
            }
        },
    })

    const msg3Animation = useSpring({
        opacity: scrollProgress >= thresholds.msg3 || hasAnimated.msg3 ? 1 : 0,
        transform: scrollProgress >= thresholds.msg3 || hasAnimated.msg3 ? 'translateY(0px)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        onRest: () => {
            if (scrollProgress >= thresholds.msg3) {
                setHasAnimated((prev) => ({ ...prev, msg3: true }))
            }
        },
    })

    const msg4Animation = useSpring({
        opacity: scrollProgress >= thresholds.msg4 || hasAnimated.msg4 ? 1 : 0,
        transform: scrollProgress >= thresholds.msg4 || hasAnimated.msg4 ? 'translateY(0px)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        onRest: () => {
            if (scrollProgress >= thresholds.msg4) {
                setHasAnimated((prev) => ({ ...prev, msg4: true }))
            }
        },
    })

    const laughAnimation = useSpring({
        opacity: scrollProgress >= thresholds.laugh || hasAnimated.laugh ? 1 : 0,
        transform: scrollProgress >= thresholds.laugh || hasAnimated.laugh ? 'translateY(0px)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        onRest: () => {
            if (scrollProgress >= thresholds.laugh) {
                setHasAnimated((prev) => ({ ...prev, laugh: true }))
            }
        },
    })

    const replyAnimation = useSpring({
        opacity: scrollProgress >= thresholds.reply || hasAnimated.reply ? 1 : 0,
        transform: scrollProgress >= thresholds.reply || hasAnimated.reply ? 'translateY(0px)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        onRest: () => {
            if (scrollProgress >= thresholds.reply) {
                setHasAnimated((prev) => ({ ...prev, reply: true }))
            }
        },
    })

    const fireAnimation = useSpring({
        opacity: scrollProgress >= thresholds.fire || hasAnimated.fire ? 1 : 0,
        transform: scrollProgress >= thresholds.fire || hasAnimated.fire ? 'translateY(0px)' : 'translateY(20px)',
        config: { tension: 280, friction: 60 },
        onRest: () => {
            if (scrollProgress >= thresholds.fire) {
                setHasAnimated((prev) => ({ ...prev, fire: true }))
            }
        },
    })

    const animationMap = useMemo(
        () => ({
            msg0: msg0Animation,
            msg1: msg1Animation,
            msg2: msg2Animation,
            msg3: msg3Animation,
            msg4: msg4Animation,
            laugh: laughAnimation,
            reply: replyAnimation,
            fire: fireAnimation,
        }),
        [
            msg0Animation,
            msg1Animation,
            msg2Animation,
            msg3Animation,
            msg4Animation,
            laughAnimation,
            replyAnimation,
            fireAnimation,
        ]
    )

    useEffect(() => {
        if (!chatRef.current) return

        const calculateScrollProgress = () => {
            if (!chatRef.current) return

            const rect = chatRef.current.getBoundingClientRect()
            const windowHeight = window.innerHeight

            let progress = 1 - (rect.bottom - windowHeight * 0.2) / (rect.height + windowHeight * 0.8)
            progress = Math.max(0, Math.min(1, progress))

            setScrollProgress(progress)

            const newAnimatedState = { ...hasAnimated }
            let stateChanged = false

            Object.entries(thresholds).forEach(([key, threshold]) => {
                const animKey = key as AnimationKey
                if (progress >= threshold && !hasAnimated[animKey]) {
                    newAnimatedState[animKey] = true
                    stateChanged = true
                }
            })

            if (stateChanged) {
                setHasAnimated(newAnimatedState)
            }
        }

        calculateScrollProgress()

        window.addEventListener('scroll', calculateScrollProgress)

        return () => {
            window.removeEventListener('scroll', calculateScrollProgress)
        }
    }, [hasAnimated, thresholds])

    useEffect(() => {
        const timer = setTimeout(() => {
            const allAnimated = Object.values(hasAnimated).every((v) => v)
            if (!allAnimated) {
                setScrollProgress(1)
            }
        }, 5000)

        return () => clearTimeout(timer)
    }, [hasAnimated])

    return (
        <SectionContainer title="" borderRadius="0" backgroundColor={theme.colors.transparent}>
            <ChatWrapper ref={chatRef}>
                <ChatContainer>
                    <Stack gutter={10} className="chat-bubbles">
                        {messages.slice(0, 5).map((message, index) => {
                            const animKey = `msg${index}` as AnimationKey
                            return (
                                <AnimatedChatBubble key={index} isUser={true} style={animationMap[animKey]}>
                                    <Body2 color={theme.colors.white}>{message.text}</Body2>
                                </AnimatedChatBubble>
                            )
                        })}
                    </Stack>

                    <AnimatedCounterWrapper isUser style={animationMap.laugh}>
                        <span role="img" aria-label="laugh emoji">
                            😂
                        </span>
                        <Body2 fontWeight={650} fontSize={17} color={theme.colors.white}>
                            18.2K
                        </Body2>
                    </AnimatedCounterWrapper>

                    <AnimatedChatBubble isUser={false} style={animationMap.reply}>
                        <Body2 color={theme.colors.white}>{messages[5].text}</Body2>
                    </AnimatedChatBubble>

                    <AnimatedCounterWrapper isUser={false} style={animationMap.fire}>
                        <span role="img" aria-label="fire emoji">
                            🔥
                        </span>
                        <Body2 fontWeight={500} fontSize={18} color={theme.colors.white}>
                            170.4K
                        </Body2>
                    </AnimatedCounterWrapper>
                </ChatContainer>
            </ChatWrapper>
        </SectionContainer>
    )
}

export default HomepageChat
